import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";
import PropTypes from "prop-types";
import { ReactMultiEmail } from "react-multi-email";
import QRcodeImage from "../../assets/images/preview-email-qr.jpg";
import { EMAIL_PATTERN_REGEX,  RouteName } from "../../constants/Constants";
import history from "../../history";
import { Localize } from "./Localize";
import { handleErrorMsgToastVisiblity, openUrlInBrowser,  resetDataForSession } from "../../shared/utility";
import {
  createSessionReset,
  inviteSession,
  inviteSessionReset,
} from "../../actions/sessionListAction";
import CustomToast from "../UI/customToast";
import { announce } from "../UI/LiveRegion";
import { Localize as AriaLocalize } from "../../shared/ariaLabelLocalize";
import { PopupModal } from "../UI/Modal/Modal";
import { ButtonWithLoader } from "../UI/Button/Button";
import ContactPicker from "../AddressBook/ContactPicker";
import AddEditContactModal from "../AddressBook/ContactModal/AddEditContactModal";
import useContactsPermissions from "../AddressBook/useContactsPermissions";

// WCAG 1.1.1 (W-08 audit): the backend-provided email template may include
// <img> tags (e.g. HubSpot-hosted logos) without an alt attribute. Before
// showing it in the agent-side preview, add a decorative alt="" to any <img>
// that lacks one so axe/SRs don't flag it. Uses DOMParser instead of a regex
// so quoting variations (alt=x, alt = "x", <IMG>) don't slip through, and so
// pathological template shapes (nested quotes, entity-encoded attributes)
// parse correctly. Recipients see the actual server-rendered email — this
// only touches the local preview.
const ensureImgAlt = (html) => {
  if (typeof window === 'undefined' || !window.DOMParser || !html) return html;
  const doc = new window.DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img:not([alt])').forEach((img) => img.setAttribute('alt', ''));
  return doc.body.innerHTML;
};

const SendEmail = ({
  id,
  isDefaultInvite,
  sessionDetailData,
  sendUrl,
  agentUrl,
  userData,
  configData,
  isUploadOnlySessionValue,
  emailRef,
  isInviteAPICalledRef,
  isSessionOwner,
  isForSaskTail,
  selectedContact,
  onSelectContact,
  onClearContact,
  pickerCache,
  onPickerCacheUpdate,
  inviteTargetSlot,
}) => {
  const dispatch = useDispatch();
  const { inviteSessionsLoading, inviteSessionsData, inviteSessionsError } =
    useSelector((state) => state.sessionList);
  const { language } = useSelector((state) => state);

  const [emails, setEmails] = useState(emailRef.current);
  const [focused, setFocused] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [isUserInvited, setIsUserInvited] = useState(false);
  const [inviteButton, setInviteButton] = useState("");
  const [localisedValues, setLocalisedValues] = useState({});
  const [isInviteAPICalled, setIsInviteAPICalled] = useState(
    isInviteAPICalledRef.current
  );
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(true)
  const [showSaveContactModal, setShowSaveContactModal] = useState(false);
  // Prevents the stale-closure in onEmailChangeHandler from disabling the button when
  // ReactMultiEmail fires onChangeInput("") after a contact email is programmatically added
  const skipNextInputChangeRef = useRef(false);
  const emailGroupRef = useRef(null);
  const { canAdd, isContactsEnabled } = useContactsPermissions();

  const emaiRregex = new RegExp(EMAIL_PATTERN_REGEX);
  const emailSentSuccessMessage = localisedValues["email_sent_label"];

  useEffect(()=>{
    setFocused(true);
    if(emails?.length > 0){ // sasktel case 
      onEmailChangeHandler(emails[0]);
    }
  },[]);

  useEffect(() => {
    emailRef.current = emails;

    if(emails.length === 0){
      setIsButtonsDisabled(true)
    }
  }, [emails]);

  useEffect(() => {
    isInviteAPICalledRef.current = isInviteAPICalled;
  }, [isInviteAPICalled]);

  useEffect(() => {
    const curLang = language.selectedlanguage;
    setLocalisedValues(Localize[curLang.id]);
  }, [language.selectedlanguage]);

  // ReactMultiEmail's internal <input> has no accessible name; tag it with
  // the same label the visible group heading exposes so screen readers announce it.
  useEffect(() => {
    const groupEl = emailGroupRef.current;
    if (!groupEl) return;
    const input = groupEl.querySelector('input');
    if (input) {
      const labelText = localisedValues["enter_email_send_invite"] || "Email addresses";
      input.setAttribute('aria-label', labelText);
      input.setAttribute('autocomplete', 'email');
      input.setAttribute('aria-required', 'true');
    }
  }, [localisedValues]);

  // Update email when selected contact changes
  useEffect(() => {
    if (selectedContact?.email) {
      const contactEmail = selectedContact.email;
      if (!emails.includes(contactEmail)) {
        // Skip the stale-closure onChangeInput("") that ReactMultiEmail fires after adding the chip
        skipNextInputChangeRef.current = true;
        setEmails((prev) => [...prev, contactEmail]);
        setIsButtonsDisabled(false);
      }
    } else if (selectedContact === null) {
      setEmails([]);
      setIsButtonsDisabled(true);
    }
  }, [selectedContact]);

  // Save as New Contact handlers
  const handleOpenSaveContact = () => {
    setShowSaveContactModal(true);
  };

  const handleCloseSaveContact = () => {
    setShowSaveContactModal(false);
  };

  const handleSaveContactSuccess = ({ savedContact } = {}) => {
    if (showSaveContactModal) CustomToast.success(localisedValues["contact_added"]);
    setShowSaveContactModal(false);
    if (savedContact && onSelectContact) {
      onSelectContact(savedContact);
    }
  };

  const currentEmailForSave = emails.length > 0 ? emails[emails.length - 1] : "";
  const showSaveAsContact = !isButtonsDisabled && !selectedContact;
  const isSaveAsContactVisible = showSaveAsContact && canAdd && isContactsEnabled;

  useEffect(() => {
    if (isSaveAsContactVisible) announce((AriaLocalize[language?.selectedlanguage?.id] || AriaLocalize['en_US']).save_as_new_contact_available);
  }, [isSaveAsContactVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (inviteSessionsLoading) return;

    if (inviteSessionsData) {
      if (isUploadOnlySessionValue) {
        CustomToast.success(
          `${emailSentSuccessMessage} ${localisedValues["recipients_can_upload_files"]}`
        );
        history.replace(RouteName.SESSIONS);
      } else {
        CustomToast.success(localisedValues["invite_sent_successfully"]);
      }
      const emailInput = document.getElementById("emailInput");
      if(emailInput){
        emailInput.focus();
      }
      setEmails([]);
      if (onClearContact) onClearContact();
      setIsUserInvited(true);
      setIsInviteAPICalled(true);
      if(!isForSaskTail) dispatch(createSessionReset());

      if (inviteButton === "InviteAndJoin") {
        openUrlInBrowser(agentUrl, "_self");
        resetDataForSession();
      }
    } else if (inviteSessionsError) {
      handleErrorMsgToastVisiblity(inviteSessionsError.message);
    }
    dispatch(inviteSessionReset());
  }, [inviteSessionsLoading, inviteSessionsData, inviteSessionsError]);

  let emailTemplateStartTime = "";
  let emailTemplateEndTime = "";
  if (sessionDetailData.session?.sessionType === 2) {
    if (sessionDetailData.session?.startAt != null) {
      const utcStartDate = moment.utc(
        sessionDetailData.session.startAt,
        "YYYY-MM-DD hh:mm:ss"
      );
      emailTemplateStartTime = utcStartDate
        .local()
        .format("ddd, MMMM DD, YYYY hh:mm A");
    }
    if (sessionDetailData.session?.endAt != null) {
      const utcEndDate = moment.utc(
        sessionDetailData.session.endAt,
        "YYYY-MM-DD hh:mm:ss"
      );
      emailTemplateEndTime = utcEndDate
        .local()
        .format("ddd, MMMM DD, YYYY hh:mm A");
    }
  }

  const emailTemplateSenderName = sessionDetailData.emailTemplate?.senderName
    .replace(/{{Company Name}}/g, userData?.domainName)
    .replace(/{{Host First Name}}/g, configData?.FNAME)
    .replace(/{{Host Last Name}}/g, configData?.LNAME);

  const emailTemplateSubject = sessionDetailData.emailTemplate?.emailSubject
    .replace(/{{Company Name}}/g, userData?.domainName)
    .replace(/{{Host First Name}}/g, configData?.FNAME)
    .replace(/{{Host Last Name}}/g, configData?.LNAME)
    .replace(/{{Reference}}/g, sessionDetailData?.session?.reference);

  const hostEmailAddress =
    (emaiRregex.test(userData?.userName) && userData.userName) ||
    configData?.NOTIFYEMAIL || "";

  const emailBody =
    '<div id="preview-email-body-content" style="word-break: break-word;">' +
    sessionDetailData?.emailTemplate?.emailBody +
    "</div>";
  const emailTemplateBody = ensureImgAlt(
    emailBody
      .replace(/{{Company Name}}/g, userData?.domainName)
      .replace(
        /{{Reference}}/g,
        sessionDetailData ? sessionDetailData?.session?.reference : ""
      )
      .replace(/{{Host First Name}}/g, configData?.FNAME)
      .replace(/{{Host Last Name}}/g, configData?.LNAME)
      .replace(/{{Host Email Address}}/g, hostEmailAddress)
      .replace(/{{Session Link}}/g, sendUrl)
      .replace(/{{QR Code}}/g, "<img src=" + QRcodeImage + ' alt="QR Code" />')
      .replace(/{{Start Time \(Scheduled only\)}}/g, emailTemplateStartTime)
      .replace(/{{End Time \(Scheduled only\)}}/g, emailTemplateEndTime)
  );

  const openEmailTemplatePreviewModal = () => {
    setShowEmailPreviewModal(true);
  };

  const previewModalBody = (
    <div className="preview-email-box custom-scroll-div">
      <div className="preview-email-head">
        <p>
          <strong>{localisedValues["sender_email"]}:</strong>{" "}
          {sessionDetailData.emailTemplate?.senderEmail}
        </p>
        <p>
          <strong>{localisedValues["sender_name"]}:</strong>{" "}
          {emailTemplateSenderName}
        </p>
        <p>
          <strong>{localisedValues["subject"]}:</strong> {emailTemplateSubject}
        </p>
        <p>
          <strong>{localisedValues["body"]}</strong>
        </p>
      </div>
      {/* Rendered inline (not in an iframe): the deployed CSP frame-src blocks
          srcDoc/about:blank/blob: frames, so an iframe preview stays blank on
          the server. emailTemplateBody is our own trusted, server-controlled
          template, so dangerouslySetInnerHTML is safe here. */}
      <div
        className="preview-email-body"
        dangerouslySetInnerHTML={{ __html: emailTemplateBody }}
      ></div>
    </div>
  );

  const previewModalFooter = (
    <div className="button-inline d-flex">
      <div className="button">
        <button
          type="button"
          className="btn btn-primary font-weight-500"
          data-bs-dismiss="modal"
          onClick={() => setShowEmailPreviewModal(false)}
        >
          {localisedValues["close"]}
        </button>
      </div>
    </div>
  );

  const inviteViaEmailHandler = (buttonType) => {
    if (emails.length > 0) {
      const request = {
        invitationType: 3,
        sessionId: sessionDetailData.session?.sessionId,
        isoCode: "",
        emailAddress: emails.join(),
        url: sendUrl,
      };
      if (selectedContact?.contactId) {
        request.contactId = selectedContact.contactId;
      }
      dispatch(inviteSession(request));
      setInviteButton(buttonType);
    }
  };

  const onEmailChangeHandler = (value) => {
    if (skipNextInputChangeRef.current) {
      skipNextInputChangeRef.current = false;
      return;
    }
    const updatedValue = value?.replace(",", "")?.trim();
    const isValidEmailId = updatedValue ? emaiRregex.test(updatedValue) : emails.length > 0;

    setIsUserInvited(false);
    setIsButtonsDisabled(!isValidEmailId);
    setIsValidEmail(true);

    if(value === "" && emails.length !== 0){
      setIsButtonsDisabled(false)
    }
  };

  return (
    <>
      <div
        className={clsx("tab-pane", isDefaultInvite ? "active show" : "")}
        id={id}
        data-testid={id}
        role="tabpanel"
        aria-labelledby="invite-email-tab"
      >
        <div className="guest-invite-box">
          <div className="guest-invite-body guest-invite-email-body">
            <div className="guest-invite-email-body-in">
              <div className="title">
                <h2 className="title-mob">{localisedValues["email"]}</h2>
                <h2 className="title-desktop">
                  {localisedValues["enter_email_send_invite"]}
                </h2>
              </div>
              {/* Address Book Contact Picker */}
              {onSelectContact && (
                <ContactPicker
                  selectedContact={selectedContact}
                  onSelectContact={onSelectContact}
                  onClearContact={onClearContact}
                  showEmail
                  pickerCache={pickerCache}
                  onPickerCacheUpdate={onPickerCacheUpdate}
                />
              )}
              <div
                className="form-group"
                role="group"
                aria-labelledby="emailInput-label"
                aria-describedby={!isValidEmail && !focused ? "emailInput-error" : undefined}
                ref={emailGroupRef}
              >
                <span id="emailInput-label" className="visually-hidden">
                  {localisedValues["enter_email_send_invite"]}
                </span>
                <ReactMultiEmail
                  id="emailInput"
                  placeholder={localisedValues["new_email_address"]}
                  emails={emails}
                  // Intentional: focus first field when the invite panel opens.
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={true}
                  className="multiple-email custom-scroll-div-thin"
                  onChange={(_emails) => {
                    console.log(_emails);
                    setEmails(_emails);
                  }}
                  onChangeInput={onEmailChangeHandler}
                  validateEmail={(email) => {
                    setIsValidEmail(emaiRregex.test(email));
                    setIsButtonsDisabled(!emaiRregex.test(email));
                    return emaiRregex.test(email);
                  }}
                  onFocus={() => {
                    setFocused(true);
                  }}
                  onBlur={() => setFocused(false)}
                  getLabel={(email, index, removeEmail) => {
                    return (
                      <div data-tag key={index} className="email-id">
                        <div data-tag-item className="email-id-text">
                          {email}
                        </div>
                        <button
                          type="button"
                          data-tag-handle
                          className="btn remove-email-btn"
                          onClick={() => removeEmail(index)}
                          aria-label={`Remove ${email}`}
                        >
                          <span className="btn-icon icon-close" aria-hidden="true"></span>
                        </button>
                      </div>
                    );
                  }}
                />
                {!isValidEmail && !focused && (
                  <div id="emailInput-error" role="alert" className="validation-error">
                    {localisedValues["enter_valid_email"]}
                  </div>
                )}
                {isSaveAsContactVisible && (
                  <button
                    type="button"
                    className="btn-save-as-contact"
                    onClick={handleOpenSaveContact}
                  >
                    <span className="for-icon icon-plus-round" aria-hidden="true"></span>
                    {localisedValues["save_as_new_contact"]}
                  </button>
                )}
              </div>
              {/* Save as New Contact Modal */}
              <AddEditContactModal
                isOpen={showSaveContactModal}
                contact={null}
                onClose={handleCloseSaveContact}
                onSuccess={handleSaveContactSuccess}
                initialEmail={currentEmailForSave}
              />

              <div className="email-preview-btn text-center">
                <button
                  className="btn btn-link text-dec-none font-weight-600"
                  type="button"
                  data-bs-target="#preview-email-modal"
                  onClick={openEmailTemplatePreviewModal}
                >
                  {localisedValues["preview_email"]}
                </button>
              </div>
              {isUserInvited && (
                <div className="send-success">
                  <span className="send-icon icon-checked-fill"></span>
                  <span className="send-text">{emailSentSuccessMessage}</span>
                </div>
              )}
            </div>
          </div>
          {/* KBD-15 (A11Y-14): radio slot between fields and footer buttons. */}
          {inviteTargetSlot}
          <div className="guest-invite-footer">
            <div className="button-inline text-center d-flex">
              <div className="button join-call-btn">
                {isInviteAPICalled && !isUploadOnlySessionValue && isSessionOwner && (
                  <div className="join-call-btn-in">
                    <button
                      type="button"
                      className="btn btn-link font-weight-500"
                      onClick={() => {openUrlInBrowser(agentUrl, "_self");resetDataForSession();}}
                    >
                      {localisedValues["join_call"]}
                    </button>
                  </div>
                )}
              </div>
              {!isUploadOnlySessionValue ? (
                <>
                  <div className="button">
                    <ButtonWithLoader
                      data-testid="InviteButton"
                      className="btn btn-light-primary btn-with-icon font-weight-500 hover-boxshadow loader-sm-space"
                      name={localisedValues["invite"]}
                      disable={isButtonsDisabled}
                      showLoader={
                        inviteButton === "Invite" && inviteSessionsLoading
                      }
                      onClick={() => inviteViaEmailHandler("Invite")}
                    />
                  </div>
                  {isSessionOwner && <div className="button">
                    <ButtonWithLoader
                      className="btn btn-primary btn-with-icon font-weight-500 hover-boxshadow loader-sm-space"
                      name={localisedValues["invite_and_join"]}
                      disable={isButtonsDisabled}
                      showLoader={
                        inviteButton === "InviteAndJoin" &&
                        inviteSessionsLoading
                      }
                      onClick={() => inviteViaEmailHandler("InviteAndJoin")}
                    />
                  </div>}
                </>
              ) : (
                <div className="button">
                  <ButtonWithLoader
                    className="btn btn-primary btn-with-icon font-weight-500 hover-boxshadow loader-sm-space"
                    name={localisedValues["send_link"]}
                    disable={isButtonsDisabled}
                    showLoader={
                      inviteButton === "Invite" && inviteSessionsLoading
                    }
                    onClick={() => inviteViaEmailHandler("Invite")}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <!-- Preview email modal --> */}
      {showEmailPreviewModal && (
        <PopupModal
          closeModal={() => setShowEmailPreviewModal(false)}
          dialogClass="modal-dialog-centered"
          modalClass="preview-email-modal alert-cms-modal"
          headerContent={
            <h2 className="modal-title">{localisedValues["preview_email"]}</h2>
          }
          bodyContent={previewModalBody}
          footerContent={previewModalFooter}
          backdrop="static"
          disablekeyboard={true}
        />
      )}
    </>
  );
};

SendEmail.propTypes = {
  id: PropTypes.string,
  isDefaultInvite: PropTypes.bool,
  sessionDetailData: PropTypes.object,
  sendUrl: PropTypes.string,
  agentUrl: PropTypes.string,
  userData: PropTypes.object,
  isUploadOnlySessionValue: PropTypes.bool,
  emailRef: PropTypes.object,
  isInviteAPICalledRef: PropTypes.object,
  configData: PropTypes.object,
  selectedContact: PropTypes.shape({
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    dialCode: PropTypes.string,
    email: PropTypes.string,
    isFavorite: PropTypes.bool,
  }),
  onSelectContact: PropTypes.func,
  onClearContact: PropTypes.func,
  pickerCache: PropTypes.shape({
    items: PropTypes.array,
    totalCount: PropTypes.number,
  }),
  onPickerCacheUpdate: PropTypes.func,
  isForSaskTail: PropTypes.bool,
  isSessionOwner: PropTypes.bool,
  inviteTargetSlot: PropTypes.node,
};

export default SendEmail;
